import { formatHours } from '@/helpers/format'
import type { PercentageRange, ProjectPhase } from '@/types'
import type { Ref } from 'vue'

/**
 * Interface defining the shape of service calculator stores.
 * All role-specific calculators (architect, engineer, etc.) implement this.
 */
export interface ServiceCalculator {
  // State properties
  estimatedTotalHours: number

  // Getters (accessed as properties in Pinia)
  getBasicServiceHours: number
  getTotalPercent: number
  getDefaultPhases: ProjectPhase[]

  // Getter functions
  getPercentageRangeForPhase: (index: number) => PercentageRange | null
  getHoursPerPhase: (index: number) => number
  getTotalHoursPerPhase: (phaseIndex: number) => number
  getServiceHours: (phaseIndex: number, subPhaseIndex: number) => number[] | null
  getAdjustedServiceHours: (phaseIndex: number, subPhaseIndex: number) => number[] | null
  getServiceHoursSum: (phaseIndex: number, subPhaseIndex: number) => number
}

/**
 * Type for modelValue containing phases array (typically a Vue ref)
 */
export interface PhasesModelValue {
  phases: ProjectPhase[]
}

/**
 * Translation function type
 */
export type TranslateFunction = (key: string) => string

/**
 * Widget text function type (variadic path)
 */
export type WidgetTextFn = (...path: string[]) => string

export const isMainPhase = function (index: number, country: string): boolean {
  //TODO: adjust for country-specific phases if needed
  if (country === 'CH') {
    return index > 1 && index < 5
  } else if (country === 'LU') {
    return true
  }

  return index > 1 && index < 5
}

export const isInRange = function (
  index: number,
  modelValue: PhasesModelValue | Ref<PhasesModelValue> | undefined,
  serviceCalculator: ServiceCalculator
): boolean {
  const phases =
    'value' in (modelValue ?? {})
      ? (modelValue as Ref<PhasesModelValue>).value?.phases
      : (modelValue as PhasesModelValue | undefined)?.phases
  if (!phases?.[index]?.active) {
    // if phase is not active, ignore values out of range
    return true
  }
  const inputPercentage = phases[index].percentage as number
  const range: PercentageRange | null = getPhasePercentageRange(index, serviceCalculator)
  if (range) {
    return (inputPercentage - range.min) * (inputPercentage - range.max) <= 0
  }
  return false
}

export const getPhasePercentageRange = function (
  index: number,
  serviceCalculator: ServiceCalculator
): PercentageRange | null {
  return serviceCalculator.getPercentageRangeForPhase(index) || null
}

export const imposeMinMax = function (index: number, modelValue: PhasesModelValue | undefined): void {
  if (!modelValue?.phases?.[index]) return
  const value = modelValue.phases[index].percentage
  if (value != undefined) {
    if (value > 100) {
      modelValue.phases[index].percentage = 100
    }
    if (value < 0) {
      modelValue.phases[index].percentage = 0
    }
  }
}

// Format just for display purposes
export const formatInitialHours = function (total: number): string {
  return formatHours(total, 100)
}

export const formatTotalHours = function (serviceCalculator: ServiceCalculator): string {
  return formatHours(serviceCalculator.getBasicServiceHours, 100)
}

export const formatHoursPerPhase = function (index: number, serviceCalculator: ServiceCalculator): string {
  return formatHours(serviceCalculator.getHoursPerPhase(index), 100)
}

export const getTotalHoursPerPhase = function (index: number, serviceCalculator: ServiceCalculator): number {
  return serviceCalculator.getTotalHoursPerPhase(index)
}

export const getServiceHours = function (
  phaseIndex: number,
  subPhaseIndex: number,
  serviceCalculator: ServiceCalculator
): number[] | null {
  return serviceCalculator.getServiceHours(phaseIndex, subPhaseIndex)
}

export const getAdjustedServiceHours = function (
  phaseIndex: number,
  subPhaseIndex: number,
  serviceCalculator: ServiceCalculator
): number[] | null {
  return serviceCalculator.getAdjustedServiceHours(phaseIndex, subPhaseIndex)
}

export const getServiceHoursSum = function (
  phaseIndex: number,
  subPhaseIndex: number,
  serviceCalculator: ServiceCalculator
): number {
  return serviceCalculator.getServiceHoursSum(phaseIndex, subPhaseIndex)
}

export const formatPhaseHours = function (hours: number, threshold: number = 100): string {
  return formatHours(hours, threshold)
}

export const getAccordionTitle = function (phase: number, country: string, wt: WidgetTextFn): string {
  const title = wt('phases', 'label')
  const phaseDescription = wt('phases', country, 'phase' + phase, 'name')
  return `${title} ${phase} <span class="hidden sm:inline" >${phaseDescription}</span>`
}

export const getAccordionSubTitle = function (
  phase: number,
  subPhase: number,
  country: string,
  wt: WidgetTextFn
): string {
  const title = wt('phases', 'label')
  const phaseName = wt('phases', country, 'phase' + phase, 'name')
  const subPhaseName = wt('phases', country, 'phase' + phase, 'subPhases', 'sub' + subPhase)
  return `${title} ${phase}${subPhase} ${phaseName}: ${subPhaseName}`
}

export interface PhaseConfiguration {
  showSubPhases: boolean
  showAdditionalEfforts: boolean
  showPercentages: boolean
}

export const getPhasesConfiguration = (
  serviceCalculator: ServiceCalculator,
  country: string = 'CH'
): PhaseConfiguration[] => {
  const defaultPhases = serviceCalculator.getDefaultPhases
  if (!defaultPhases || defaultPhases.length === 0) {
    // Fallback to default configuration
    return [
      { showSubPhases: false, showAdditionalEfforts: true, showPercentages: false },
      { showSubPhases: false, showAdditionalEfforts: true, showPercentages: false },
      { showSubPhases: true, showAdditionalEfforts: false, showPercentages: false },
      { showSubPhases: true, showAdditionalEfforts: false, showPercentages: false },
      { showSubPhases: true, showAdditionalEfforts: false, showPercentages: false },
      { showSubPhases: false, showAdditionalEfforts: true, showPercentages: false },
    ]
  }

  return defaultPhases.map((phase: ProjectPhase) => {
    const shouldShowSingleSubPhase = country === 'CH' && phase.order === 4
    const hasSubPhases = Boolean(phase.subPhases && (phase.subPhases.length > 1 || shouldShowSingleSubPhase))

    return {
      showSubPhases: hasSubPhases,
      showAdditionalEfforts: !hasSubPhases,
      // When a phase is split into sub-phases, percentages are edited per sub-phase.
      // Rendering the parent percentage editor would incorrectly bind to sub-phase 1.
      showPercentages: !hasSubPhases && !!phase.percentage,
    }
  })
}
